import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, LogOut } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import {
  AUTH_TOKEN_CHANGED,
  ImpersonationClaim,
  exitSignInAs,
  readImpersonation,
} from '../services/impersonation';

/** mm:ss, never negative. */
export function countdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Read-only "Sign in as" banner (2026-10-26, docs/security/ADMIN_SIGN_IN_AS.md).
 *
 * Restored from the token's own claim on every load, so a reload never leaves the administrator looking like the
 * person with no banner. Exit asks the server to end the sign-in-as and return the administrator's session, then
 * reloads into /admin/users. When the clock runs out it exits by itself.
 */
function SignInAsBanner({ claim }: { claim: ImpersonationClaim }) {
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const autoExited = useRef(false);
  const left = claim.expiresAt - now;

  const exit = useCallback(async () => {
    setBusy(true);
    setProblem(null);
    const r = await exitSignInAs();
    if (r.ok) {
      window.location.assign('/admin/users');
      return;
    }
    if (r.status === undefined) {
      setProblem(r.message || 'The server could not be reached. Try Exit again.');
      setBusy(false);
      return;
    }
    window.location.assign('/login');
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    // Once: if that attempt cannot reach the server, the Exit button stays for the administrator to retry.
    if (left <= 0 && !autoExited.current) {
      autoExited.current = true;
      void exit();
    }
  }, [left, exit]);

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="impersonation-banner"
      className="sticky top-0 left-0 right-0 z-[9999] flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2 shadow-lg"
      style={{
        background: 'var(--tide-hero, #0b2a2e)',
        color: 'var(--tide-on-hero, #ffffff)',
        borderBottom: '3px solid var(--tide-amber, #e8a33a)',
      }}
    >
      <div className="flex min-w-0 items-center gap-2 text-sm">
        <Eye className="h-4 w-4 flex-shrink-0" aria-hidden="true" style={{ color: 'var(--tide-amber, #e8a33a)' }} />
        <span className="min-w-0">
          Viewing as <strong className="font-semibold">{claim.targetName}</strong> — read-only.{' '}
          <span className="whitespace-nowrap" style={{ color: 'var(--tide-hero-ink-2, #b9dcd5)' }}>
            Ends in <span className="font-mono tabular-nums" aria-label={`${Math.max(0, Math.ceil(left / 60000))} minutes left`}>{countdown(left)}</span>.
          </span>
          {problem && <span role="alert" className="ml-2 font-medium" style={{ color: 'var(--tide-amber, #e8a33a)' }}>{problem}</span>}
        </span>
      </div>
      <button
        type="button"
        onClick={() => void exit()}
        disabled={busy}
        className="flex min-h-[36px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 text-xs font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60"
        style={{
          background: 'var(--tide-interact, #2ec4b0)',
          color: 'var(--tide-interact-ink, #0b2a2e)',
          outlineColor: 'var(--tide-focus-on-hero, #2ec4b0)',
        }}
      >
        <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
        {busy ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  );
}

/** The older sandbox impersonation (other tenants): React state only, unchanged. */
function SandboxBanner() {
  const { impersonating, exitImpersonation } = useUser();
  const navigate = useNavigate();
  if (!impersonating) return null;
  const handleExit = () => {
    exitImpersonation();
    navigate('/admin/sandbox/tenants');
  };
  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white px-4 py-2 flex items-center justify-between gap-4 shadow-lg">
      <div className="flex items-center gap-2 text-sm font-medium min-w-0">
        <Eye className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">
          Viewing as <strong>{impersonating.targetEmail}</strong>
          {impersonating.sandboxTenantId && (
            <span className="font-normal opacity-80"> — sandbox: <code className="font-mono">{impersonating.sandboxTenantId}</code></span>
          )}
        </span>
      </div>
      <button
        onClick={handleExit}
        className="flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0"
      >
        <LogOut className="w-3.5 h-3.5" />
        Exit Impersonation
      </button>
    </div>
  );
}

export function ImpersonationBanner() {
  const [claim, setClaim] = useState<ImpersonationClaim | null>(() => readImpersonation());

  useEffect(() => {
    const sync = () => setClaim(readImpersonation());
    window.addEventListener(AUTH_TOKEN_CHANGED, sync);
    window.addEventListener('storage', sync); // another tab started or ended one
    return () => {
      window.removeEventListener(AUTH_TOKEN_CHANGED, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  if (claim) return <SignInAsBanner claim={claim} />;
  return <SandboxBanner />;
}

export default ImpersonationBanner;
